import { useEffect, useRef, useState } from "react";
import { Camera, FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, del, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDialog } from "./ConfirmDialog";
import { PhotoViewer } from "./PhotoViewer";
import { Button } from "@/components/ui/button";

const MAX = 10 * 1024 * 1024;
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,application/pdf";

const Thumb = ({ file, onLoaded, onView }) => {
  const [url, setUrl] = useState(null);
  const isImage = file.content_type.startsWith("image/");
  useEffect(() => {
    let objectUrl;
    if (isImage) {
      api.get(`/files/${file.id}`, { responseType: "blob" }).then((r) => {
        objectUrl = URL.createObjectURL(r.data);
        setUrl(objectUrl);
        onLoaded(file.id, objectUrl);
      }).catch(() => {
        setUrl("error");
        onLoaded(file.id, "error");
      });
    }
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file.id, isImage]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async () => {
    if (isImage) return onView(file.id);
    try {
      const r = await api.get(`/files/${file.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank", "noopener");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  return (
    <button type="button" onClick={open} title={file.original_filename} data-testid={`file-thumb-${file.id}`}
      className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-100 transition-shadow hover:shadow-md">
      {isImage && url && url !== "error" ? <img src={url} alt={file.original_filename} className="h-full w-full object-cover" />
        : isImage && !url ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        : <FileText className="h-6 w-6 text-slate-500" />}
    </button>
  );
};

export const Attachments = ({ address, runId, runFiles, testId, onChanged, canUpload = true, canDelete = true, title = "Photos & files" }) => {
  const { profile } = useAuth();
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [urls, setUrls] = useState({});
  const [viewing, setViewing] = useState(null);
  const files = address ? address.files || [] : runFiles || [];
  const photos = files.filter((f) => f.content_type.startsWith("image/"));
  const onLoaded = (id, url) => setUrls((u) => ({ ...u, [id]: url }));
  const onView = (id) => setViewing(photos.findIndex((p) => p.id === id));

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    const tooBig = list.find((f) => f.size > MAX);
    if (tooBig) return toast.error(`${tooBig.name} is larger than 10 MB`);
    setUploading(true);
    try {
      for (const f of list) {
        const body = new FormData();
        body.append("file", f);
        await api.post(address ? `/addresses/${address.id}/files` : `/runs/${runId}/files`, body);
      }
      toast.success(list.length > 1 ? `${list.length} files uploaded` : "File uploaded");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
      onChanged();
    } finally {
      setUploading(false);
    }
  };

  const remove = async () => {
    try {
      await del(`/files/${toDelete.id}`);
      toast.success("File removed");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err));
      return false;
    }
  };

  return (
    <div className="mt-4" data-testid={`${testId}-files`}>
      <p className="text-sm font-medium text-slate-700">{title}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {files.map((f) => (
          <div key={f.id} className="group relative">
            <Thumb file={f} onLoaded={onLoaded} onView={onView} />
            {canDelete && (profile.role === "manager" || f.uploaded_by === profile.id) && (
              <button type="button" onClick={() => setToDelete(f)} aria-label={`Remove ${f.original_filename}`} data-testid={`file-delete-${f.id}`}
                className="absolute -right-1.5 -top-1.5 flex h-7 w-7 items-center justify-center rounded-full bg-rose-600 text-white shadow">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        ))}
        {canUpload && <>
          <Button type="button" variant="outline" disabled={uploading} onClick={() => input.current?.click()}
            className="h-20 w-20 flex-col gap-1 border-dashed text-xs" data-testid={`${testId}-upload-button`}>
            {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Camera className="h-5 w-5" />}
            {uploading ? "Uploading" : "Add"}
          </Button>
          <input ref={input} type="file" accept={ACCEPT} multiple hidden onChange={upload} data-testid={`${testId}-file-input`} />
        </>}
      </div>
      {viewing !== null && viewing >= 0 && photos[viewing] && (
        <PhotoViewer photos={photos} urls={urls} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} />
      )}
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title="Remove this file?"
        description={`${toDelete?.original_filename} will be removed from this ${address ? "address" : "warrant"}.`} confirmLabel="Remove file" onConfirm={remove} />
    </div>
  );
};
